import React from "react";
import { Link } from "react-router-dom";
import { Clock } from "lucide-react";
import { formatDate } from "@/lib/api";

export function InterviewCard({ interview, variant = "default", testId }) {
  const { slug, title, deck, guest_name, guest_role, company, thumbnail, reading_time, topics = [], published_at, industry_slug } = interview;
  if (variant === "feature") {
    return (
      <Link
        to={`/interviews/${slug}`}
        data-testid={testId || `interview-card-${slug}`}
        className="group block"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
          <div className="relative aspect-[4/3] md:aspect-[5/4] overflow-hidden bg-oc-sand">
            <img
              src={thumbnail}
              alt={title}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700 ease-out"
            />
          </div>
          <div className="flex flex-col justify-center">
            <div className="eyebrow text-oc-coral mb-4">
              <span>Featured Interview</span>
              {industry_slug && <span className="text-oc-ink-muted"> / {industry_slug.replace(/-/g, " ")}</span>}
            </div>
            <h3 className="font-editorial text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight text-oc-ink group-hover:text-oc-blue transition-colors">
              {title}
            </h3>
            <p className="mt-6 font-editorial text-lg text-oc-ink-soft leading-relaxed line-clamp-3">
              {deck}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 font-ui text-sm text-oc-ink-muted">
              <span className="font-semibold text-oc-ink">{guest_name}</span>
              {guest_role && <span>· {guest_role}</span>}
              {reading_time && <span className="flex items-center gap-1.5">· <Clock className="w-3.5 h-3.5" /> {reading_time} min read</span>}
            </div>
          </div>
        </div>
      </Link>
    );
  }
  if (variant === "compact") {
    return (
      <Link
        to={`/interviews/${slug}`}
        data-testid={testId || `interview-card-${slug}`}
        className="group flex gap-4 items-start"
      >
        <div className="w-24 h-24 flex-shrink-0 overflow-hidden bg-oc-sand">
          <img src={thumbnail} alt={title} loading="lazy" className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="eyebrow text-oc-coral mb-1">{industry_slug?.replace(/-/g, " ")}</div>
          <h4 className="font-editorial text-lg leading-tight text-oc-ink group-hover:text-oc-blue transition-colors line-clamp-3">
            {title}
          </h4>
          <div className="mt-2 font-ui text-xs text-oc-ink-muted">{guest_name}</div>
        </div>
      </Link>
    );
  }
  return (
    <Link
      to={`/interviews/${slug}`}
      data-testid={testId || `interview-card-${slug}`}
      className="group flex flex-col bg-white border border-oc-hairline hover:border-oc-ink transition-colors"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-oc-sand">
        <img
          src={thumbnail}
          alt={title}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700 ease-out"
        />
      </div>
      <div className="p-6 md:p-8 flex flex-col flex-1">
        <div className="eyebrow text-oc-coral mb-3">
          {industry_slug?.replace(/-/g, " ") || "Interview"}
        </div>
        <h3 className="font-editorial text-2xl leading-tight text-oc-ink group-hover:text-oc-blue transition-colors">
          {title}
        </h3>
        {deck && (
          <p className="mt-3 font-editorial text-base text-oc-ink-soft leading-relaxed line-clamp-3">
            {deck}
          </p>
        )}
        <div className="mt-6 pt-6 border-t border-oc-hairline flex items-center justify-between font-ui text-xs text-oc-ink-muted">
          <div>
            <div className="font-semibold text-oc-ink">{guest_name}</div>
            <div>{company || guest_role}</div>
          </div>
          {reading_time && (
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" /> {reading_time} min
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}

export function OperatorCard({ operator, testId }) {
  const { slug, name, portrait, role, company, industry_slug, short_bio } = operator;
  return (
    <Link
      to={`/operators/${slug}`}
      data-testid={testId || `operator-card-${slug}`}
      className="group block bg-white border border-oc-hairline hover:border-oc-ink transition-colors"
    >
      <div className="relative aspect-square overflow-hidden bg-oc-sand">
        <img
          src={portrait}
          alt={name}
          loading="lazy"
          className="w-full h-full object-cover object-center group-hover:scale-[1.03] transition-transform duration-500"
        />
      </div>
      <div className="p-6">
        {industry_slug && <div className="eyebrow text-oc-coral mb-2">{industry_slug.replace(/-/g, " ")}</div>}
        <div className="font-ui font-bold text-lg text-oc-ink group-hover:text-oc-blue transition-colors">{name}</div>
        <div className="font-ui text-sm text-oc-ink-muted mt-1">{role}{company ? ` · ${company}` : ""}</div>
        {short_bio && <p className="mt-4 font-editorial text-sm text-oc-ink-soft line-clamp-3">{short_bio}</p>}
      </div>
    </Link>
  );
}

export function TopicCard({ topic, testId }) {
  return (
    <Link
      to={`/topics/${topic.slug}`}
      data-testid={testId || `topic-card-${topic.slug}`}
      className="group flex flex-col p-8 border border-oc-hairline bg-white hover:border-oc-ink hover:bg-oc-ink hover:text-white transition-colors"
    >
      <div className="eyebrow text-oc-coral group-hover:text-oc-coral mb-3">Topic</div>
      <div className="font-editorial text-2xl md:text-3xl leading-tight">{topic.name}</div>
      {topic.definition && (
        <p className="mt-3 font-editorial text-sm text-oc-ink-muted group-hover:text-white/70 line-clamp-3">
          {topic.definition}
        </p>
      )}
      {topic.why_it_matters && (
        <div className="mt-5 pt-5 border-t border-oc-hairline group-hover:border-white/20">
          <div className="eyebrow text-oc-ink-muted group-hover:text-white/60 mb-2">Why it matters</div>
          <p className="font-editorial text-sm leading-snug text-oc-ink-soft group-hover:text-white/80 line-clamp-3">
            {topic.why_it_matters}
          </p>
        </div>
      )}
    </Link>
  );
}

export function IndustryCard({ industry, testId }) {
  return (
    <Link
      to={`/industries/${industry.slug}`}
      data-testid={testId || `industry-card-${industry.slug}`}
      className="group relative block aspect-[4/5] overflow-hidden bg-oc-ink"
    >
      {industry.hero_image && (
        <img
          src={industry.hero_image}
          alt={industry.name}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-50 group-hover:scale-105 transition-all duration-700"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-oc-ink via-oc-ink/30 to-transparent" />
      <div className="relative h-full flex flex-col justify-end p-6 md:p-8 text-white">
        <div className="eyebrow text-oc-coral mb-2">Industry</div>
        <div className="font-editorial text-2xl md:text-3xl leading-tight">{industry.name}</div>
      </div>
    </Link>
  );
}

export function CollectionCard({ collection, testId }) {
  return (
    <Link
      to={`/collections/${collection.slug}`}
      data-testid={testId || `collection-card-${collection.slug}`}
      className="group flex flex-col md:flex-row bg-white border border-oc-hairline hover:border-oc-ink transition-colors overflow-hidden"
    >
      {collection.featured_image && (
        <div className="relative md:w-1/2 aspect-[4/3] md:aspect-auto overflow-hidden bg-oc-sand">
          <img
            src={collection.featured_image}
            alt={collection.title}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
          />
        </div>
      )}
      <div className="p-8 md:p-12 md:w-1/2 flex flex-col justify-center">
        <div className="eyebrow text-oc-coral mb-3">Collection · {collection.interview_slugs?.length || 0} interviews</div>
        <div className="font-editorial text-2xl md:text-3xl leading-tight text-oc-ink group-hover:text-oc-blue transition-colors">
          {collection.title}
        </div>
        {collection.description && (
          <p className="mt-4 font-editorial text-base text-oc-ink-soft leading-relaxed line-clamp-3">
            {collection.description}
          </p>
        )}
      </div>
    </Link>
  );
}

export function SectionHeader({ eyebrow, title, description, action, testId }) {
  return (
    <div data-testid={testId} className="mb-10 md:mb-14 flex flex-col md:flex-row md:items-end justify-between gap-6">
      <div className="max-w-3xl">
        {eyebrow && <div className="eyebrow text-oc-coral mb-4">{eyebrow}</div>}
        <h2 className="font-editorial text-3xl md:text-5xl leading-[1.05] tracking-tight text-oc-ink">{title}</h2>
        {description && (
          <p className="mt-4 font-editorial text-lg text-oc-ink-soft max-w-2xl leading-relaxed">{description}</p>
        )}
      </div>
      {action}
    </div>
  );
}

export function LoadingBlock() {
  return (
    <div className="min-h-[40vh] flex items-center justify-center">
      <div className="w-12 h-12 border-2 border-oc-ink border-t-transparent animate-spin" />
    </div>
  );
}

export { formatDate };
